'use client';
import { useId, useSyncExternalStore } from 'react';
import { Card, CardDescription, CardHeader, CardTitle } from '../../components/ui/shadcn/card';
import { Button } from '../../components/ui/Button';
import { openBrainComposer } from '../../lib/brainDock';
import { heroGreeting, recapVariants, staticHeroPrompts, type HeroPrompt } from '../../lib/heroCopy';
import { useTranslation } from '../../lib/i18n';
import { useDashRecap, useMe } from '../../lib/queries';
import type { DashRecap } from '../../lib/types';
import { meta as chatMeta } from '../chat/meta';

const subscribeNever = () => () => {};
const PROMPT_CARDS = 4;

/** A new conversation's opening screen: the dashboard hero's greeting and ask over the same prompt
 *  seeds, drawn as shadcn cards. The agent-written pills of today's first recap variant take the cards
 *  over; without them the static seeds stand in. A card only seeds the composer through the existing
 *  compose channel, so the person can edit the text before sending it. On the full page the greeting
 *  uses the dashboard's display role; the narrow dock keeps the page-title role. A phone shows only the
 *  centred greeting and ask, because four stacked cards pushed them off the first screen. */
export function ChatStart({ variant, recapSeed = null }: { variant: 'compact' | 'full'; recapSeed?: DashRecap | null }) {
  const { t } = useTranslation();
  const user = useMe().data?.user;
  const recap = recapVariants(useDashRecap(recapSeed).data)[0];
  // The time-of-day greeting belongs to the reader's clock, which the server render does not know.
  const hydrated = useSyncExternalStore(subscribeNever, () => true, () => false);
  const greeting = heroGreeting(t.dashboard, hydrated ? new Date().getHours() : undefined, user, recap);
  const seeds = staticHeroPrompts(t.dashboard);
  const prompts: HeroPrompt[] = (recap?.pills?.length ? recap.pills : [seeds.summary, seeds.plan, seeds.find, seeds.capabilities])
    .slice(0, PROMPT_CARDS);

  return (
    <section aria-labelledby="chat-start-greeting" data-testid="chat-start" className="@container w-full max-md:text-center">
      <h2 id="chat-start-greeting" className={`${variant === 'full' ? 'text-display' : 'text-page-title'} text-foreground`}>
        {greeting === null ? '\u00a0' : <>{greeting}<span aria-hidden className="text-primary">.</span></>}
      </h2>
      <p className="mt-3 text-lead text-muted-foreground">{recap?.ask?.trim() || t.dashboard.heroAsk}</p>
      <div role="group" aria-label={t.dashboard.quickActions} className="mt-8 grid grid-cols-1 gap-3 @lg:grid-cols-2 max-md:hidden">
        {prompts.map((prompt, i) => <PromptCard key={i} prompt={prompt} dock={variant === 'compact'} />)}
      </div>
    </section>
  );
}

/** One prompt as a shadcn Card. The card is the pointer target through the title's stretched button,
 *  which stays the one real control for the keyboard and reads the prompt as its description. In the dock
 *  (`dock`) the seed stays in the dock's own composer rather than moving to the /chat page. */
function PromptCard({ prompt, dock }: { prompt: HeroPrompt; dock: boolean }) {
  const descriptionId = useId();
  const Icon = chatMeta.icon;
  return (
    <Card className="relative h-full transition-colors hover:bg-accent/40 has-[button:focus-visible]:ring-2 has-[button:focus-visible]:ring-ring">
      <CardHeader>
        <CardTitle>
          <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            title={prompt.prompt}
            aria-describedby={descriptionId}
            onClick={() => openBrainComposer(prompt.prompt, { dock })}
            className="h-auto min-w-0 justify-start truncate rounded-sm px-0 text-left text-raised-foreground hover:bg-transparent hover:text-raised-foreground after:absolute after:inset-0 after:rounded-xl"
          >
            <span className="truncate text-section-title">{prompt.label}</span>
          </Button>
        </CardTitle>
        <CardDescription id={descriptionId} className="line-clamp-2">{prompt.prompt}</CardDescription>
      </CardHeader>
    </Card>
  );
}
